import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Package, AlertTriangle, XCircle, CheckCircle, RefreshCw, Save, LayoutDashboard, Archive, Mail, Database, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryPage = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newStock, setNewStock] = useState('');
  const [reseeding, setReseeding] = useState(false);
  const [potmConfig, setPotmConfig] = useState({ product_id: '', tagline: 'This month\'s ritual', enabled: true });
  const [savingPotm, setSavingPotm] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem('adminKey')) {
      navigate('/admin/login');
      return;
    }
    fetchInventory();
    fetchPotm();
  }, [navigate]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/inventory/status`);
      setInventory(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchPotm = async () => {
    try {
      const { data } = await axios.get(`${API}/peach-of-month`);
      setPotmConfig({
        product_id: data?.product?.id || '',
        tagline: data?.tagline || 'This month\'s ritual',
        enabled: data?.enabled ?? true,
      });
    } catch (error) {
      console.error('Failed to fetch peach of month:', error);
    }
  };

  const savePotm = async (overrides = {}) => {
    setSavingPotm(true);
    try {
      const payload = { ...potmConfig, ...overrides };
      await axios.put(`${API}/admin/peach-of-month`, payload, {
        headers: { 'X-Admin-Key': sessionStorage.getItem('adminKey') || '' },
      });
      toast.success('Peach of the Month updated');
      fetchPotm();
    } catch (error) {
      if (error?.response?.status === 401) {
        sessionStorage.removeItem('adminKey');
        navigate('/admin/login');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to update');
      }
    } finally {
      setSavingPotm(false);
    }
  };

  const handleUpdateStock = async (productId) => {
    const stockValue = parseInt(newStock, 10);
    if (isNaN(stockValue) || stockValue < 0) {
      toast.error('Please enter a valid stock number');
      return;
    }

    try {
      await axios.put(`${API}/inventory/${productId}?stock=${stockValue}`);
      toast.success('Stock updated successfully');
      setEditingProduct(null);
      setNewStock('');
      fetchInventory();
    } catch (error) {
      console.error('Failed to update stock:', error);
      toast.error('Failed to update stock');
    }
  };

  const handleReseedProducts = async () => {
    if (!window.confirm(
      'Reset the entire product catalogue to the canonical seed data?\n\n' +
      'This will REPLACE all current products with the latest pricing, descriptions, variants and bundles.\n\n' +
      'Existing orders are NOT affected. Continue?'
    )) return;
    setReseeding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reseed-products`,
        {},
        { headers: { 'X-Admin-Key': sessionStorage.getItem('adminKey') || '' } }
      );
      toast.success(`Catalogue refreshed — ${data.inserted_count} products inserted (${data.deleted_count} replaced)`);
      fetchInventory();
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error('Session expired — please log in again');
        sessionStorage.removeItem('adminKey');
        navigate('/admin/login');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to reseed products');
      }
    } finally {
      setReseeding(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'out_of_stock':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'low_stock':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'out_of_stock':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-50 text-red-600">Out of Stock</span>;
      case 'low_stock':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-600">Low Stock</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-50 text-green-600">In Stock</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-perfect-peach" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-white">
      <Helmet>
        <title>Inventory Management | The Perfect Peach Club</title>
      </Helmet>

      {/* Admin Top Bar */}
      <div className="bg-[#2D2420] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <span className="font-playfair text-lg font-semibold text-perfect-peach">PPC Admin</span>
            <nav className="hidden sm:flex items-center gap-1">
              <Link to="/admin" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <Link to="/admin/inventory" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white">
                <Archive className="w-4 h-4" /> Inventory
              </Link>
              <Link to="/admin/subscribers" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                <Mail className="w-4 h-4" /> Subscribers
              </Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">View Store</Link>
            <button onClick={() => { sessionStorage.removeItem('adminKey'); navigate('/admin/login'); }} className="text-white/60 hover:text-white text-sm transition-colors">Logout</button>
          </div>
        </div>
      </div>

      {/* Mobile admin nav */}
      <div className="sm:hidden bg-[#2D2420] border-t border-white/10 px-4 pb-2 flex gap-2 overflow-x-auto">
        <Link to="/admin" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/70">
          <LayoutDashboard className="w-4 h-4" /> Dashboard
        </Link>
        <Link to="/admin/inventory" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 text-white">
          <Archive className="w-4 h-4" /> Inventory
        </Link>
        <Link to="/admin/subscribers" className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white/70">
          <Mail className="w-4 h-4" /> Subscribers
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-playfair text-2xl sm:text-3xl font-semibold text-[#2D2420] mb-1">
              Inventory Management
            </h1>
            <p className="text-african-earth text-sm">
              Monitor and manage your product stock levels
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReseedProducts}
              disabled={reseeding}
              className="flex items-center gap-2 bg-perfect-peach text-white px-4 py-2 rounded-full shadow-soft hover:shadow-floating transition-all disabled:opacity-60"
              data-testid="reseed-products-btn"
              title="Replace all products with the latest canonical pricing & descriptions"
            >
              <Database className="w-4 h-4" />
              {reseeding ? 'Refreshing…' : 'Refresh Catalogue'}
            </button>
            <button
              onClick={fetchInventory}
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-soft hover:shadow-floating transition-all text-african-earth"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-african-earth">In Stock</span>
            </div>
            <p className="text-3xl font-bold text-[#2D2420]">{inventory?.summary?.in_stock || 0}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <span className="text-african-earth">Low Stock</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{inventory?.summary?.low_stock || 0}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-soft">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <span className="text-african-earth">Out of Stock</span>
            </div>
            <p className="text-3xl font-bold text-red-500">{inventory?.summary?.out_of_stock || 0}</p>
          </div>
        </div>

        {/* Peach of the Month Config */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-bone-white flex items-center gap-2">
            <Star className="w-4 h-4 text-coral" />
            <h2 className="font-playfair text-xl font-semibold text-[#2D2420]">Peach of the Month</h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-african-earth uppercase tracking-wide mb-1.5">Featured Product</label>
              <select
                value={potmConfig.product_id}
                onChange={(e) => setPotmConfig({ ...potmConfig, product_id: e.target.value })}
                className="w-full px-3 py-2 border border-[#E0D5D0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coral"
                data-testid="potm-product-select"
              >
                <option value="">— Auto-pick top bundle —</option>
                {inventory?.products?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} · R{p.price?.toFixed(2)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-african-earth uppercase tracking-wide mb-1.5">Tagline</label>
              <input
                type="text"
                value={potmConfig.tagline}
                onChange={(e) => setPotmConfig({ ...potmConfig, tagline: e.target.value })}
                placeholder="Your February ritual"
                maxLength={60}
                className="w-full px-3 py-2 border border-[#E0D5D0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coral"
                data-testid="potm-tagline-input"
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <input
                  type="checkbox"
                  checked={potmConfig.enabled}
                  onChange={(e) => setPotmConfig({ ...potmConfig, enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-[#E0D5D0] accent-coral"
                  data-testid="potm-enabled-checkbox"
                />
                <span className="text-sm text-african-earth">
                  {potmConfig.enabled ? 'Showing on home' : 'Hidden'}
                </span>
              </label>
              <button
                onClick={() => savePotm()}
                disabled={savingPotm}
                className="bg-coral text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-coral/90 transition-colors disabled:opacity-60"
                data-testid="potm-save-btn"
              >
                {savingPotm ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="px-6 py-4 border-b border-bone-white">
            <h2 className="font-playfair text-xl font-semibold text-[#2D2420]">
              All Products
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-bone-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-african-earth uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-african-earth uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-african-earth uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-african-earth uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-african-earth uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bone-white">
                {inventory?.products?.map((product) => (
                  <tr key={product.id} className="hover:bg-bone-white/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(product.status)}
                        <span className="font-medium text-[#2D2420]">{product.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-african-earth">
                      {product.category}
                    </td>
                    <td className="px-6 py-4">
                      {editingProduct === product.id ? (
                        <input
                          type="number"
                          min="0"
                          value={newStock}
                          onChange={(e) => setNewStock(e.target.value)}
                          className="w-20 px-3 py-1 border border-[#E0D5D0] rounded-lg focus:outline-none focus:ring-2 focus:ring-perfect-peach"
                          autoFocus
                        />
                      ) : (
                        <span className={`font-semibold ${product.stock === 0 ? 'text-red-500' : product.stock <= 5 ? 'text-amber-600' : 'text-[#2D2420]'}`}>
                          {product.stock}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(product.status)}
                    </td>
                    <td className="px-6 py-4">
                      {editingProduct === product.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleUpdateStock(product.id)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <Save className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingProduct(null);
                              setNewStock('');
                            }}
                            className="text-african-earth hover:text-red-500"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setEditingProduct(product.id);
                            setNewStock(product.stock.toString());
                          }}
                          className="text-perfect-peach hover:text-peach-dark font-medium text-sm"
                        >
                          Update Stock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryPage;
